import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Ban,
  CalendarClock,
  CheckCircle2,
  Download,
  Eye,
  FileText,
  Info,
  Loader2,
  Lock,
  Mail,
  PauseCircle,
  Power,
  PowerOff,
  Send,
  SkipForward,
  Upload,
  Wrench,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useBreadcrumb } from '@/components/common/BreadcrumbContext';
import LockedCcChips from '@/components/common/LockedCcChips';
import { StateCitySelect } from '@/components/common/StateCitySelect';
import { LiveAumStatus } from '@/components/renewals/LiveAumStatus';
import { StatusChip, type ChipTone } from '@/components/sales/StatusChip';
import NumberStepper from '@/components/ui/NumberStepper';
import { useToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select-menu';
import { Textarea } from '@/components/ui/textarea';
import { SETTLED_PAYMENT_MODES } from '@/features/sales-rm/leadForm';
import {
  useAbortDeactivation,
  useCancelSubscription,
  useCompleteRenewal,
  useNotifyRenewalService,
  useRecordRenewalInvoice,
  useReactivateRenewal,
  useRecordRenewalPayment,
  useRefreshRenewalAum,
  useRenewal,
  useSendDeactivationEmail,
  useSendRenewalInvoice,
  useSendRenewalTermination,
  useSkipRenewal,
  useStartDeactivation,
  useUpdateRenewalClientDetails,
  useUpdateRenewalDueDate,
  useUpdateRenewalPricing,
  useVerifyRenewal,
} from '@/hooks/useRenewals';
import { inr } from '@/lib/sales-metrics';
import { fileToBase64 } from '@/services/billing.service';
import {
  renewalsService,
  type DeactivationAudience,
  type RenewalClientDetailsPayload,
  type RenewalDetail,
  type RenewalReminderKind,
} from '@/services/renewals.service';
import { errorMessage } from '@/utils/apiError';
import cn from '@/utils/cn';
import {
  BILLER_CANCELLATION_REASONS,
  QUERY_KEYS,
  RENEWAL_CANCELLATION_REASON,
  RENEWAL_FREQUENCIES,
  ROUTES,
  type RenewalCancellationReasonValue,
  type RenewalFrequencyValue,
  type RenewalStatusValue,
} from '@/utils/constants';
import {
  normalizeDiscountValue,
  resolveLineDiscountAmount,
  type LineDiscountMode,
} from '@/utils/productTermPricing';

// Slug of the seeded client-facing renewal invoice template (mirrors the
// server's RENEWAL_INVOICE_TEMPLATE_SLUG).
const INVOICE_TEMPLATE_SLUG = 'renewal-invoice';

const STEPS = [
  { key: 'details', title: 'Client Details', short: 'Confirm contact, GST & AUM' },
  { key: 'pricing', title: 'Pricing & Frequency', short: 'Set the amount & verify' },
  { key: 'schedule', title: 'Emails & Payment', short: 'Email ladder · record payment' },
  { key: 'invoice', title: 'Generate Invoice', short: 'Upload the invoice PDF' },
  { key: 'notify', title: 'Notify Service', short: 'Notify the service queue' },
  { key: 'send', title: 'Send Invoice', short: 'Email the invoice' },
  { key: 'complete', title: 'Renewal Complete', short: 'Close & schedule next cycle' },
] as const;

type StepKey = (typeof STEPS)[number]['key'];
type StepStatus = 'completed' | 'current' | 'pending' | 'blocked';

const FREQUENCY_DIVISOR: Record<RenewalFrequencyValue, number> = {
  Annual: 1,
  Quarterly: 4,
  Monthly: 12,
};

const PERIOD_NOUN: Record<RenewalFrequencyValue, string> = {
  Annual: 'year',
  Quarterly: 'quarter',
  Monthly: 'month',
};

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// DATEONLY strings (due dates, invoice dates, reminder slots) - no time part.
const fmtDate = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const localTodayIso = (): string => {
  const n = new Date();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  return `${n.getFullYear()}-${m}-${d}`;
};

const doneFlags = (d: RenewalDetail): Record<StepKey, boolean> => ({
  // Step 1 completes when the biller SAVES the client block, which stamps
  // clientDetailsConfirmedAt. It used to derive from verifiedAt like step 2, so
  // saving step 1 left it un-ticked and both steps went green at once on verify.
  // verifiedAt is still accepted as a fallback for rows confirmed pre-migration.
  details: !!d.clientDetailsConfirmedAt || !!d.verifiedAt,
  pricing: !!d.verifiedAt,
  schedule: !!d.payment.paidAt,
  invoice: !!d.invoice.invoiceRecordedAt,
  notify: !!d.serviceNotification.serviceNotifiedAt,
  send: !!d.clientInvoice.sentAt,
  complete: !!d.completion.completedAt,
});

const firstIncompleteIndex = (d: RenewalDetail): number => {
  const flags = doneFlags(d);
  const idx = STEPS.findIndex((s) => !flags[s.key]);
  return idx === -1 ? STEPS.length - 1 : idx;
};

const isTerminated = (d: RenewalDetail): boolean => d.status === 'Service Terminated';

/**
 * The subscription is switched off. The server refuses every workflow write on
 * such a row, so the page has to lock the same set of controls - otherwise each
 * button is just a way to produce a 409 toast.
 */
const isDeactivated = (d: RenewalDetail): boolean => d.deactivation.deactivatedAt != null;

/** Non-ARN client opted out at step 1 (ADKJ-117): the cycle is closed. */
const isSkipped = (d: RenewalDetail): boolean => d.skip.skippedAt != null;

/** Nothing in the renewal workflow may be advanced in any of these states. */
const isFrozen = (d: RenewalDetail): boolean => isTerminated(d) || isDeactivated(d) || isSkipped(d);

const isOverdue = (d: RenewalDetail): boolean => {
  if (d.payment.paidAt || d.completion.completedAt || isFrozen(d)) return false;
  return d.dueDate.slice(0, 10) < localTodayIso();
};

const STEP_ICON: Record<StepStatus, ComponentType<{ className?: string }>> = {
  completed: CheckCircle2,
  current: Loader2,
  pending: AlertCircle,
  blocked: Lock,
};

const STEP_DOT: Record<StepStatus, string> = {
  completed: 'bg-emerald-500 text-white',
  current: 'bg-yellow-400 text-black',
  pending: 'bg-muted text-muted-foreground',
  blocked: 'bg-muted text-muted-foreground',
};

const renewalTone = (status: RenewalStatusValue): ChipTone => {
  if (status === 'Renewal Complete') return 'success';
  if (status === 'Service Terminated') return 'destructive';
  // Matches STATUS_TONE on the queue page - the same row must not be red in one
  // view and blue in the other.
  if (status === 'Deactivated') return 'destructive';
  if (status === 'Skipped') return 'neutral';
  if (status === 'Pending Verification') return 'warning';
  return 'info';
};

// ─── Presentational helpers (billing workflow idioms) ────────────────────────
const HeaderCell = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={cn('break-words text-sm font-semibold text-foreground', mono && 'font-mono')}>
      {value}
    </p>
  </div>
);

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="min-w-0 rounded-lg border border-border bg-background p-4">
    <p className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</p>
    {children}
  </div>
);

const ReadOnly = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0 space-y-1">
    <p className="text-[11px] font-semibold text-muted-foreground">{label}</p>
    <p className="break-words text-sm font-semibold">{value}</p>
  </div>
);

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const TextField = ({ label, value, onChange, disabled, placeholder }: TextFieldProps) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Input
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

// ─── Per-step audit metadata (merged activity timeline) ──────────────────────
interface StepMeta {
  at: string | null;
  by: string | null;
  detail?: string;
}

const stepMetaFor = (d: RenewalDetail, key: StepKey): StepMeta => {
  switch (key) {
    case 'details':
      return {
        at: d.verifiedAt,
        by: d.verifiedByName,
        detail: d.verifiedAt ? 'Locked on verification' : undefined,
      };
    case 'pricing':
      return { at: d.verifiedAt, by: d.verifiedByName, detail: d.verifyNotes || undefined };
    case 'schedule':
      return {
        at: d.payment.paidAt,
        by: d.payment.recordedByName,
        detail: d.payment.transactionNo
          ? `${d.payment.transactionNo} · ${d.payment.mode ?? '-'}`
          : undefined,
      };
    case 'invoice':
      return {
        at: d.invoice.invoiceRecordedAt,
        by: d.invoice.invoiceRecordedByName,
        detail:
          d.invoice.invoiceNumber && d.invoice.invoiceAmount != null
            ? `${d.invoice.invoiceNumber} · ${inr(d.invoice.invoiceAmount)}`
            : undefined,
      };
    case 'notify':
      return {
        at: d.serviceNotification.serviceNotifiedAt,
        by: d.serviceNotification.serviceNotifiedByName,
        detail: d.serviceNotification.serviceQueueRef
          ? `Queue ${d.serviceNotification.serviceQueueRef}`
          : undefined,
      };
    case 'send':
      return {
        at: d.clientInvoice.sentAt,
        by: d.clientInvoice.sentByName,
        detail: d.clientInvoice.sentAt ? `Emailed to ${d.client.email}` : undefined,
      };
    case 'complete':
      return {
        at: d.completion.completedAt,
        by: d.completion.completedByName,
        detail: d.completion.completedAt ? 'Next cycle spawned' : undefined,
      };
    default:
      return { at: null, by: null };
  }
};

interface StepProps {
  detail: RenewalDetail;
}

interface AdvanceStepProps extends StepProps {
  onDone: () => void;
}

// Step 1 Skip control (Non-ARN clients only, ADKJ-117): certain client types
// are not in the renewal-reminder flow at all, so the biller can close the
// cycle here - every later step is bypassed and no next cycle is spawned.
const SkipRenewalControl = ({ detail }: StepProps) => {
  const { toast } = useToast();
  const skipRenewal = useSkipRenewal(detail.id);
  const [confirming, setConfirming] = useState(false);

  if (!detail.skip.canSkip) return null;

  const submit = () => {
    skipRenewal.mutate(undefined, {
      onSuccess: () => {
        toast.success('Renewal skipped');
        setConfirming(false);
      },
      onError: (error) => toast.error(errorMessage(error, 'Could not skip this renewal')),
    });
  };

  if (!confirming) {
    return (
      <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
        <SkipForward className="mr-1 h-3.5 w-3.5" />
        Skip Renewal
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 p-2">
      <span className="text-[11px] text-muted-foreground">
        Skip this cycle? No reminders are sent and no next cycle is created.
      </span>
      <Button size="sm" variant="danger" onClick={submit} disabled={skipRenewal.isPending}>
        {skipRenewal.isPending ? 'Skipping…' : 'Confirm Skip'}
      </Button>
      <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
        Keep
      </Button>
    </div>
  );
};

// ─── Step 1: Client Details ──────────────────────────────────────────────────
const StepClientDetails = ({ detail, onDone }: AdvanceStepProps) => {
  const { toast } = useToast();
  const save = useUpdateRenewalClientDetails(detail.id);
  const c = detail.client;
  const editable = !detail.verifiedAt;

  const [form, setForm] = useState({
    primaryEmail: c.primaryEmail ?? '',
    primaryNumber: c.primaryNumber ?? '',
    secondaryEmail: c.secondaryEmail ?? '',
    secondaryNumber: c.secondaryNumber ?? '',
    gstin: c.gstin ?? '',
    legalName: c.legalName ?? '',
    displayName: c.displayName ?? '',
    address: c.address ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    pincode: c.pincode ?? '',
  });
  const [arnAum, setArnAum] = useState(c.arnAum != null ? String(c.arnAum) : '');
  const [addArnAums, setAddArnAums] = useState<Record<number, string>>(() =>
    Object.fromEntries(c.additionalArns.map((r) => [r.id, r.aum != null ? String(r.aum) : ''])),
  );

  const setField = (key: keyof typeof form) => (value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = () => {
    const aumTrim = arnAum.trim();
    if (aumTrim && !/^\d{1,11}(\.\d+)?$/.test(aumTrim)) {
      toast.error('Primary ARN AUM must be a positive number (crores)');
      return;
    }
    const arnRows: { id: number; aum: number }[] = [];
    const invalid = c.additionalArns.some((row) => {
      const raw = (addArnAums[row.id] ?? '').trim();
      if (!raw) return false; // Blank = leave unchanged.
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed < 0) return true;
      arnRows.push({ id: row.id, aum: parsed });
      return false;
    });
    if (invalid) {
      toast.error('Additional ARN AUM values must be positive numbers (crores)');
      return;
    }
    const payload: RenewalClientDetailsPayload = {
      primaryEmail: form.primaryEmail.trim(),
      primaryNumber: form.primaryNumber.trim(),
      secondaryEmail: form.secondaryEmail.trim(),
      secondaryNumber: form.secondaryNumber.trim(),
      gstin: form.gstin.trim(),
      legalName: form.legalName.trim(),
      displayName: form.displayName.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      arnAum: aumTrim,
      ...(arnRows.length ? { additionalArns: arnRows } : {}),
    };
    save.mutate(payload, {
      onSuccess: () => {
        toast.success('Client details saved');
        onDone();
      },
      onError: (error) => toast.error(errorMessage(error, 'Could not save the client details')),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-extrabold">Client Details</h2>
          <p className="text-sm text-muted-foreground">
            {editable
              ? 'Confirm the contact, GST and AUM details for this renewal cycle.'
              : 'Details are locked - this renewal has been verified.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* Non-ARN clients may opt out of the whole renewal flow here. */}
          <SkipRenewalControl detail={detail} />
          {editable ? (
            <Button size="sm" onClick={submit} disabled={save.isPending}>
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
              {save.isPending ? 'Saving…' : 'Save & Continue'}
            </Button>
          ) : (
            // Locked (already verified): nothing to save, but the step must
            // still lead forward rather than dead-ending.
            <Button size="sm" variant="outline" onClick={onDone}>
              Continue
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Section title="Contact">
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Primary Email"
              value={form.primaryEmail}
              onChange={setField('primaryEmail')}
              disabled={!editable}
            />
            <TextField
              label="Primary Number"
              value={form.primaryNumber}
              onChange={setField('primaryNumber')}
              disabled={!editable}
            />
            <TextField
              label="Secondary Email"
              value={form.secondaryEmail}
              onChange={setField('secondaryEmail')}
              disabled={!editable}
            />
            <TextField
              label="Secondary Number"
              value={form.secondaryNumber}
              onChange={setField('secondaryNumber')}
              disabled={!editable}
            />
          </div>
        </Section>

        <Section title="GST Details">
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="GSTIN"
              value={form.gstin}
              onChange={setField('gstin')}
              disabled={!editable}
            />
            <TextField
              label="Legal Name"
              value={form.legalName}
              onChange={setField('legalName')}
              disabled={!editable}
            />
            <TextField
              label="Display Name"
              value={form.displayName}
              onChange={setField('displayName')}
              disabled={!editable}
            />
            <TextField
              label="Address"
              value={form.address}
              onChange={setField('address')}
              disabled={!editable}
            />
            {/* Dropdowns, not free text (Fix 3). `disabled` is passed through
                rather than swapped for a read-only variant so the field keeps
                the same shape and position whether or not the step is editable. */}
            <StateCitySelect
              state={form.state}
              city={form.city}
              onStateChange={setField('state')}
              onCityChange={setField('city')}
              disabled={!editable}
              labelClassName="text-sm font-medium"
            />
            <TextField
              label="Pincode"
              value={form.pincode}
              onChange={setField('pincode')}
              disabled={!editable}
            />
          </div>
        </Section>

        <Section title="Primary ARN">
          <div className="grid gap-3 sm:grid-cols-2">
            <ReadOnly label="ARN" value={c.arn} />
            <div className="space-y-1.5">
              <Label>AUM (Cr)</Label>
              <Input
                value={arnAum}
                disabled={!editable}
                placeholder="e.g. 125.5"
                onChange={(e) => setArnAum(e.target.value)}
              />
            </div>
          </div>
          {c.additionalArns.length > 0 && (
            <div className="mt-3 overflow-x-auto rounded-md border border-border">
              <table className="w-full min-w-[420px] text-sm">
                <thead className="bg-muted/40 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Additional ARN</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2 text-right">AUM (Cr)</th>
                  </tr>
                </thead>
                <tbody>
                  {c.additionalArns.map((row) => (
                    <tr key={row.id} className="border-t border-border">
                      <td className="px-3 py-1.5 font-mono text-xs">{row.arn}</td>
                      <td className="px-3 py-1.5">{row.clientName}</td>
                      <td className="px-3 py-1.5 text-right">
                        <Input
                          value={addArnAums[row.id] ?? ''}
                          disabled={!editable}
                          className="ml-auto h-8 w-28 text-right"
                          onChange={(e) => {
                            const { value } = e.target;
                            setAddArnAums((m) => ({ ...m, [row.id]: value }));
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {detail.pricing.isAumBased && !c.isNonArn && <LiveAumStatus detail={detail} />}
        </Section>

        <Section title="Products Owned">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-muted-foreground">
                <th className="pb-1">Product</th>
                <th className="pb-1">Code</th>
                <th className="pb-1 text-right">Term</th>
              </tr>
            </thead>
            <tbody>
              {detail.products.map((p) => (
                <tr
                  key={p.productCode}
                  className={cn(
                    'border-t border-border',
                    p.productCode === detail.productCode && 'bg-primary/5',
                  )}
                >
                  <td className="py-1.5">
                    {p.productName}
                    {p.productCode === detail.productCode && (
                      <span className="ml-1.5 rounded bg-primary px-1 text-[9px] font-bold text-primary-foreground">
                        THIS RENEWAL
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 font-mono text-xs text-muted-foreground">
                    {p.productCode}
                  </td>
                  <td className="py-1.5 text-right text-muted-foreground">
                    {p.termYears != null ? `${p.termYears} yr` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>
    </div>
  );
};

// ─── Step 2: Pricing & Frequency + Verify ────────────────────────────────────
const StepPricing = ({ detail, onDone }: AdvanceStepProps) => {
  const { toast } = useToast();
  const patch = useUpdateRenewalPricing(detail.id);
  const verify = useVerifyRenewal(detail.id);
  const p = detail.pricing;
  const locked = !!detail.verifiedAt;
  const terminated = isTerminated(detail);

  const [notes, setNotes] = useState(detail.verifyNotes ?? '');
  const [customAmount, setCustomAmount] = useState(p.customAmount ?? p.annualAmount);
  const [pendingFreq, setPendingFreq] = useState<RenewalFrequencyValue | null>(null);

  // The discount is deliberately NOT gated on `locked`: it is the lever that
  // closes a renewal mid-chase, so it stays editable after verification and
  // locks only once money is recorded (server enforces the same boundary).
  const discountLocked = !!detail.payment.paidAt || !!detail.completion.completedAt || terminated;
  const [discountMode, setDiscountMode] = useState<LineDiscountMode>(p.discountMode ?? 'percent');
  const [discountValue, setDiscountValue] = useState<string>(
    p.discountValue != null ? String(p.discountValue) : '',
  );

  const changeFrequency = (value: string) => {
    const freq = RENEWAL_FREQUENCIES.find((f) => f === value);
    if (!freq || freq === p.frequency) return;
    setPendingFreq(freq);
    patch.mutate(
      { frequency: freq },
      {
        onSuccess: () => toast.success(`Frequency set to ${freq}`),
        onError: (error) => toast.error(errorMessage(error, 'Could not change the frequency')),
        onSettled: () => setPendingFreq(null),
      },
    );
  };

  const saveAmount = () => {
    patch.mutate(
      { customAmount },
      {
        onSuccess: () => toast.success('Renewal amount saved'),
        onError: (error) => toast.error(errorMessage(error, 'Could not save the renewal amount')),
      },
    );
  };

  const doVerify = () => {
    verify.mutate(notes.trim() || undefined, {
      onSuccess: () => {
        toast.success('Renewal verified - email ladder scheduled');
        onDone();
      },
      onError: (error) => toast.error(errorMessage(error, 'Could not verify the renewal')),
    });
  };

  const applyDiscount = () => {
    const raw = Number(discountValue);
    if (!Number.isFinite(raw) || raw < 0) {
      toast.error('Enter a valid discount');
      return;
    }
    if (discountMode === 'percent' && raw > 100) {
      toast.error('A percentage discount cannot exceed 100');
      return;
    }
    patch.mutate(
      { discountMode, discountValue: normalizeDiscountValue(discountMode, raw) },
      {
        onSuccess: () => toast.success('Discount applied'),
        onError: (error) => toast.error(errorMessage(error, 'Could not apply the discount')),
      },
    );
  };

  const clearDiscount = () => {
    patch.mutate(
      { discountMode: null, discountValue: null },
      {
        onSuccess: () => {
          setDiscountValue('');
          toast.success('Discount removed');
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not remove the discount')),
      },
    );
  };

  // The server-refetched detail is authoritative; while a frequency PATCH is
  // in flight we show a local estimate so the tile doesn't lag a beat. The
  // estimate reuses the shared discount helper (the client copy kept in sync
  // per ADR-019/036) rather than re-deriving the rule here.
  const shownFreq = pendingFreq ?? p.frequency;
  const grossPerPeriod = pendingFreq
    ? Math.round(p.annualAmount / FREQUENCY_DIVISOR[pendingFreq])
    : p.grossAmount;
  const discountPerPeriod = pendingFreq
    ? resolveLineDiscountAmount(p.discountMode, p.discountValue, grossPerPeriod)
    : p.discountAmount;
  const perPeriod = Math.max(0, grossPerPeriod - discountPerPeriod);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-extrabold">Pricing & Frequency</h2>
          <p className="text-sm text-muted-foreground">
            {locked
              ? 'Pricing is locked - this renewal has been verified.'
              : 'Set the renewal amount and billing frequency, then verify.'}
          </p>
        </div>
        <Button size="sm" onClick={doVerify} disabled={verify.isPending || locked || terminated}>
          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
          {locked ? 'Verified' : 'Verify Renewal'}
        </Button>
      </div>

      {p.approvalRequired && (
        <div className="rounded-lg border border-warning/40 bg-warning-tint p-3 text-sm">
          <p className="font-bold text-ink">Approval required</p>
          <p className="text-ink">
            The combined AUM exceeds 1000 Cr - this renewal price needs management approval.
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Section title="Product">
          <div className="grid grid-cols-2 gap-3">
            <ReadOnly label="Product" value={p.productName} />
            <ReadOnly label="Code" value={p.productCode} />
            <ReadOnly label="Pricing Basis" value={p.isAumBased ? 'AUM slab' : 'Fixed'} />
            <ReadOnly
              label="Cycle"
              value={`Cycle ${detail.cycleNumber} · ${detail.termYears} yr term`}
            />
          </div>
        </Section>

        {p.isAumBased ? (
          <Section title="AUM Slab Pricing">
            <div className="grid grid-cols-2 gap-3">
              <ReadOnly
                label="Combined AUM"
                value={p.combinedAum != null ? `${p.combinedAum} Cr` : '-'}
              />
              <ReadOnly label="Slab Band" value={p.slabBand ?? '-'} />
              <ReadOnly label="Annual Renewal Price" value={inr(p.annualAmount)} />
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              The slab renewal price applies - a custom amount is not allowed for AUM-based
              products. Update the AUM in Client Details to change the slab.
            </p>
          </Section>
        ) : (
          <Section title="Annual Renewal Amount">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label>{`Annual amount · default ${inr(p.annualAmount)}`}</Label>
                <NumberStepper
                  value={customAmount}
                  onChange={setCustomAmount}
                  min={0}
                  max={1_000_000_000}
                  step={100}
                  inputWidthClassName="w-28"
                  ariaLabel="Annual renewal amount"
                  disabled={locked}
                />
              </div>
              {!locked && (
                <Button size="sm" variant="outline" onClick={saveAmount} disabled={patch.isPending}>
                  {patch.isPending ? 'Saving…' : 'Save Amount'}
                </Button>
              )}
            </div>
          </Section>
        )}

        <Section title="Billing Frequency">
          <div className="space-y-1.5">
            <Label>Frequency</Label>
            <Select
              value={shownFreq}
              disabled={locked || patch.isPending}
              onChange={(e) => changeFrequency(e.target.value)}
            >
              {RENEWAL_FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </Select>
          </div>
          <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary">
              Billed per period
            </p>
            <p className="text-2xl font-extrabold tabular-nums">{inr(perPeriod)}</p>
            <p className="text-[11px] text-muted-foreground">
              {`${inr(p.annualAmount)} / year ÷ ${FREQUENCY_DIVISOR[shownFreq]} · per ${PERIOD_NOUN[shownFreq]}`}
            </p>
            {discountPerPeriod > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {`${inr(grossPerPeriod)} − ${inr(discountPerPeriod)} discount`}
              </p>
            )}
            {/* What the client is actually asked to pay (and is invoiced). */}
            {!patch.isPending && (
              <p className="mt-1.5 border-t border-primary/20 pt-1.5 text-[11px] font-semibold text-foreground">
                {`+ ${inr(p.gstAmount)} GST @ ${p.gstPercentage}% = ${inr(p.totalAmount)} payable`}
              </p>
            )}
          </div>
        </Section>

        <Section title="Renewal Discount">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select
                value={discountMode}
                disabled={discountLocked || patch.isPending}
                onChange={(e) => setDiscountMode(e.target.value as LineDiscountMode)}
                className="w-32"
              >
                <option value="percent">Percentage</option>
                <option value="amount">Fixed amount</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{discountMode === 'percent' ? 'Discount (%)' : 'Discount (₹)'}</Label>
              <Input
                type="number"
                min={0}
                max={discountMode === 'percent' ? 100 : undefined}
                step={discountMode === 'percent' ? 0.25 : 100}
                value={discountValue}
                disabled={discountLocked || patch.isPending}
                onChange={(e) => setDiscountValue(e.target.value)}
                className="w-32"
                placeholder="0"
              />
            </div>
            {!discountLocked && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={applyDiscount}
                  disabled={patch.isPending || !discountValue.trim()}
                >
                  {patch.isPending ? 'Saving…' : 'Apply'}
                </Button>
                {p.discountAmount > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearDiscount}
                    disabled={patch.isPending}
                  >
                    Remove
                  </Button>
                )}
              </>
            )}
          </div>

          {p.discountAmount > 0 ? (
            <div className="mt-3 rounded-lg border border-success/40 bg-success-tint p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-success">
                Discount applied
              </p>
              <p className="mt-0.5 text-sm font-semibold tabular-nums">
                {`${inr(p.grossAmount)} − ${inr(p.discountAmount)} = ${inr(p.amount)} per ${PERIOD_NOUN[p.frequency]}`}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {p.discountMode === 'percent'
                  ? `${p.discountValue}% off this cycle. GST is charged on the discounted amount.`
                  : 'Fixed amount off this cycle. GST is charged on the discounted amount.'}
              </p>
            </div>
          ) : (
            <p className="mt-2 text-[11px] text-muted-foreground">
              {discountLocked
                ? 'The discount is locked once a payment is recorded.'
                : 'Applies to this renewal cycle only, before GST. Can still be applied after verification - the emails and invoice pick up the new amount.'}
            </p>
          )}
        </Section>

        <Section title="Verification Notes">
          <Textarea
            value={notes}
            disabled={locked}
            onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
            placeholder="Optional notes for the renewal audit log…"
            className="min-h-[90px]"
          />
          <div className="mt-1 text-right text-[11px] text-muted-foreground tabular-nums">
            {`${notes.length}/1000`}
          </div>
        </Section>
      </div>

      {locked ? (
        <p className="text-xs text-muted-foreground">
          {`Verified ${fmtDateTime(detail.verifiedAt)}${detail.verifiedByName ? ` by ${detail.verifiedByName}` : ''}.`}
        </p>
      ) : (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-foreground">
          Verifying locks client details & pricing and schedules the renewal notice and follow-up
          emails automatically.
        </div>
      )}
    </div>
  );
};

// ─── Step 3: Email Schedule & Payment ────────────────────────────────────────
// Only the three CHASE emails are automated. Service termination is sent by
// hand from this step (it ends the client's service and cancels the
// subscription), so it is rendered separately below the ladder table.
const LADDER: { kind: RenewalReminderKind; label: string }[] = [
  { kind: 'notice', label: 'Renewal Notice' },
  { kind: 'followup1', label: 'Follow-up 1' },
  { kind: 'followup2', label: 'Follow-up 2' },
];

const ladderChip = (
  detail: RenewalDetail,
  kind: RenewalReminderKind,
): { tone: ChipTone; label: string } => {
  const stamps: Record<RenewalReminderKind, string | null> = {
    notice: detail.emailStamps.noticeSentAt,
    followup1: detail.emailStamps.followup1SentAt,
    followup2: detail.emailStamps.followup2SentAt,
    termination: detail.emailStamps.terminationSentAt,
  };
  const stamp = stamps[kind];
  if (stamp) return { tone: 'success', label: `Sent ${fmtDate(stamp)}` };
  const reminder = detail.emailSchedule.find((r) => r.kind === kind);
  if (reminder?.status === 'pending') return { tone: 'info', label: 'Scheduled' };
  if (reminder?.status === 'failed') return { tone: 'destructive', label: 'Failed' };
  if (!detail.verifiedAt) return { tone: 'neutral', label: 'Not scheduled' };
  if (detail.payment.paidAt || detail.cancellation.cancelledAt)
    return { tone: 'neutral', label: 'Cancelled' };
  return { tone: 'neutral', label: '-' };
};

// Read-only preview of one ladder email, rendered exactly as the dispatcher
// will send it (same template, same single-product renewal table, same dates).
const LadderPreview = ({
  renewalId,
  kind,
  label,
}: {
  renewalId: number;
  kind: RenewalReminderKind;
  label: string;
}) => {
  const preview = useQuery({
    queryKey: [...QUERY_KEYS.RENEWALS.BY_ID(renewalId), 'email-preview', kind],
    queryFn: () => renewalsService.previewLadderEmail(renewalId, kind),
  });

  if (preview.isLoading) return <p className="text-sm text-muted-foreground">Loading preview…</p>;
  if (preview.isError) {
    return (
      <p className="text-sm text-destructive">
        {errorMessage(preview.error, 'Could not load this email preview.')}
      </p>
    );
  }
  if (!preview.data) return null;
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">Subject: </span>
        {preview.data.subject}
      </p>
      <iframe
        title={`${label} email preview`}
        className="h-[420px] w-full rounded-md border border-border bg-white"
        srcDoc={preview.data.bodyHtml}
      />
    </div>
  );
};

// ─── Refine "Renewal Unpaid" reason (after the fact) ─────────────────────────
// Sending the service termination sets the reason to 'Renewal Unpaid'
// automatically - that action has no reason picker of its own, because at the
// moment it fires nobody knows WHY the client stopped paying. If the biller
// later reaches the client and learns the real reason, refining it here makes
// the MIS record more accurate rather than leaving it stuck on a placeholder.
//
// Only ever shown on that one placeholder value - once refined, this panel
// disappears (the row now carries a real reason). The server enforces the
// other side of this: SYSTEM_ASSIGNED_REASONS in renewal.service.ts still
// refuses to relabel an UPGRADE closure, which has no "real reason" to
// discover the way an unpaid renewal does.
const RefineTerminationReasonPanel = ({ detail }: StepProps) => {
  const { toast } = useToast();
  const cancelSubscription = useCancelSubscription(detail.id);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<RenewalCancellationReasonValue | ''>('');
  const [note, setNote] = useState('');

  if (detail.cancellation.reason !== RENEWAL_CANCELLATION_REASON.RENEWAL_UNPAID) return null;

  const needsNote = reason === 'Other';
  const canSubmit =
    !!reason && (!needsNote || note.trim().length > 0) && !cancelSubscription.isPending;

  const close = () => {
    setOpen(false);
    setReason('');
    setNote('');
  };

  const submit = () => {
    if (!reason) return;
    cancelSubscription.mutate(
      { reason, ...(note.trim() ? { note: note.trim() } : {}) },
      {
        onSuccess: () => {
          toast.success('Reason updated');
          close();
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not update the reason')),
      },
    );
  };

  if (!open) {
    return (
      <Button
        size="sm"
        variant="ghost"
        className="mt-2 w-full text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(true)}
      >
        Know the real reason now? Refine it
      </Button>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-border bg-muted/30 p-2.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Refine cancellation reason
      </p>
      <p className="mb-2 text-[11px] text-muted-foreground">
        &quot;
        {RENEWAL_CANCELLATION_REASON.RENEWAL_UNPAID}
        &quot; was recorded automatically - the client simply stopped paying and nobody knew why at
        the time. If you have since spoken to them, record the actual reason here.
      </p>

      <div className="space-y-1.5">
        <Label className="text-[11px]">Actual reason</Label>
        <Select
          value={reason}
          onChange={(e) => setReason(e.target.value as RenewalCancellationReasonValue | '')}
          className="h-8 bg-background text-sm"
          aria-label="Refined cancellation reason"
        >
          <option value="">- Select a reason -</option>
          {BILLER_CANCELLATION_REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      </div>

      {needsNote && (
        <div className="mt-2 space-y-1.5">
          <Label className="text-[11px]">Describe the reason</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 255))}
            placeholder="What did the client say?"
            className="min-h-[60px] bg-background text-sm"
          />
          <div className="text-right text-[10px] text-muted-foreground tabular-nums">
            {`${note.length}/255`}
          </div>
        </div>
      )}

      <div className="mt-2 flex gap-2">
        <Button size="sm" className="flex-1" disabled={!canSubmit} onClick={submit}>
          {cancelSubscription.isPending ? 'Saving…' : 'Update Reason'}
        </Button>
        <Button size="sm" variant="outline" className="flex-1" onClick={close}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const StepSchedule = ({ detail, onDone }: AdvanceStepProps) => {
  const { toast } = useToast();
  const pay = useRecordRenewalPayment(detail.id);
  const changeDueDate = useUpdateRenewalDueDate(detail.id);
  const sendTermination = useSendRenewalTermination(detail.id);
  const verified = !!detail.verifiedAt;
  const paid = !!detail.payment.paidAt;
  const terminated = isTerminated(detail);

  const [txn, setTxn] = useState('');
  const [mode, setMode] = useState('');
  // Which ladder email is expanded for preview (null = none). View-only.
  const [previewKind, setPreviewKind] = useState<RenewalReminderKind | null>(null);
  // Due-date editor: only committed on Save, so a mistyped date never fires a
  // reschedule.
  const [dueDateDraft, setDueDateDraft] = useState(detail.dueDate);
  const [editingDueDate, setEditingDueDate] = useState(false);
  // A line or two on what was agreed for THIS date, logged against it so the
  // queue's Notes button can show "date -> note" (ADR-054). Cleared once a
  // payment lands, since the notes only explain a chase that is still running.
  const [dueDateNote, setDueDateNote] = useState('');
  // Termination is destructive (ends service + cancels the subscription), so it
  // goes through an explicit confirm.
  const [confirmTermination, setConfirmTermination] = useState(false);
  const [showTerminationPreview, setShowTerminationPreview] = useState(false);

  const dueDateLocked = paid || terminated || !!detail.completion.completedAt;

  const submit = () => {
    if (!txn.trim()) {
      toast.error('Enter the transaction number');
      return;
    }
    if (!mode) {
      toast.error('Select the payment mode');
      return;
    }
    pay.mutate(
      { transactionNo: txn.trim(), mode },
      {
        onSuccess: () => {
          toast.success('Payment recorded - remaining emails cancelled');
          onDone();
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not record the payment')),
      },
    );
  };

  const saveDueDate = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDateDraft)) {
      toast.error('Pick a valid due date');
      return;
    }
    // A note on its own is a legitimate save: the biller may want to record what
    // was agreed without the date itself moving.
    if (dueDateDraft === detail.dueDate && !dueDateNote.trim()) {
      setEditingDueDate(false);
      return;
    }
    changeDueDate.mutate(
      { dueDate: dueDateDraft, note: dueDateNote.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(
            dueDateDraft === detail.dueDate
              ? 'Note added against the due date'
              : 'Due date updated - pending emails rescheduled',
          );
          setEditingDueDate(false);
          setDueDateNote('');
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not update the due date')),
      },
    );
  };

  const doTerminate = () => {
    sendTermination.mutate(undefined, {
      onSuccess: () => {
        toast.success('Service termination sent - subscription cancelled');
        setConfirmTermination(false);
      },
      onError: (error) =>
        toast.error(errorMessage(error, 'Could not send the service termination')),
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-extrabold">Email Schedule & Payment</h2>
        <p className="text-sm text-muted-foreground">
          The three chase emails are automated - they start when the renewal is verified and stop
          when a payment is recorded. Service termination is sent manually.
        </p>
      </div>

      {!verified && (
        <div className="rounded-lg border border-warning/40 bg-warning-tint p-3 text-sm text-ink">
          Verify the renewal in Pricing & Frequency to schedule the email ladder and unlock payment
          recording.
        </div>
      )}

      <Section title="Renewal Due Date">
        <div className="flex flex-wrap items-end gap-3">
          {editingDueDate ? (
            <>
              <div className="space-y-1.5">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={dueDateDraft}
                  onChange={(e) => setDueDateDraft(e.target.value)}
                  className="w-44"
                />
              </div>
              <div className="min-w-[16rem] flex-1 space-y-1.5">
                <Label>
                  {dueDateDraft.slice(0, 10) !== detail.dueDate.slice(0, 10)
                    ? `Note for ${fmtDate(detail.dueDate)} → ${fmtDate(dueDateDraft)} (optional)`
                    : 'Note for this date (optional)'}
                </Label>
                <Input
                  value={dueDateNote}
                  onChange={(e) => setDueDateNote(e.target.value.slice(0, 500))}
                  placeholder="e.g. Client asked to be billed after their quarter end"
                  maxLength={500}
                />
              </div>
              <Button size="sm" onClick={saveDueDate} disabled={changeDueDate.isPending}>
                {changeDueDate.isPending ? 'Saving…' : 'Save & reschedule'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDueDateDraft(detail.dueDate);
                  setDueDateNote('');
                  setEditingDueDate(false);
                }}
                disabled={changeDueDate.isPending}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Current Due Date
                </p>
                <p className="mt-0.5 text-base font-semibold tabular-nums">
                  {fmtDate(detail.dueDate)}
                </p>
              </div>
              {!dueDateLocked && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setDueDateDraft(detail.dueDate);
                    setEditingDueDate(true);
                  }}
                >
                  <CalendarClock className="mr-1 h-3.5 w-3.5" />
                  Change due date
                </Button>
              )}
            </>
          )}
        </div>
        {detail.dueDateChangedAt && (
          <p className="mt-2 text-[11px] text-muted-foreground">
            {`Last changed ${fmtDateTime(detail.dueDateChangedAt)}${detail.dueDateChangedByName ? ` by ${detail.dueDateChangedByName}` : ''}.`}
          </p>
        )}
        <p className="mt-2 text-[11px] text-muted-foreground">
          {dueDateLocked
            ? 'The due date is locked once a payment is recorded or the subscription ends.'
            : 'Changing the due date reschedules every email that has not been sent yet. Emails already sent are left as they are.'}
        </p>

        {detail.dueDateNotes.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Due Date Notes
            </p>
            <ul className="space-y-1.5">
              {detail.dueDateNotes.map((n) => (
                <li
                  key={`${n.at}-${n.dueDate}`}
                  className="rounded-md border border-border bg-muted/30 px-2.5 py-1.5 text-xs"
                >
                  {/* "old date -> new date: note" - the date it moved from is
                      half of what the note is a decision about. */}
                  {n.fromDueDate && n.fromDueDate.slice(0, 10) !== n.dueDate.slice(0, 10) && (
                    <>
                      <span className="font-semibold tabular-nums text-muted-foreground">
                        {fmtDate(n.fromDueDate)}
                      </span>
                      <span className="mx-1.5 text-muted-foreground">→</span>
                    </>
                  )}
                  <span className="font-semibold tabular-nums">{fmtDate(n.dueDate)}</span>
                  <span className="mr-1.5 text-muted-foreground">:</span>
                  <span>{n.note}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-muted-foreground">
              These are cleared automatically once the payment is recorded.
            </p>
          </div>
        )}
      </Section>

      <Section title="Automated Email Ladder">
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[520px] whitespace-nowrap text-sm">
            <thead className="bg-muted/40 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Scheduled For</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Preview</th>
              </tr>
            </thead>
            <tbody>
              {LADDER.map((row) => {
                const reminder = detail.emailSchedule.find((r) => r.kind === row.kind);
                const chip = ladderChip(detail, row.kind);
                const open = previewKind === row.kind;
                return (
                  <Fragment key={row.kind}>
                    <tr className="border-t border-border">
                      <td className="px-3 py-2 font-semibold">{row.label}</td>
                      <td className="px-3 py-2 tabular-nums text-muted-foreground">
                        {reminder ? fmtDate(reminder.scheduledFor) : '-'}
                      </td>
                      <td className="px-3 py-2">
                        <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPreviewKind(open ? null : row.kind)}
                          aria-expanded={open}
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" />
                          {open ? 'Hide' : 'View'}
                        </Button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-t border-border bg-muted/20">
                        <td colSpan={4} className="whitespace-normal px-3 py-3">
                          <LadderPreview renewalId={detail.id} kind={row.kind} label={row.label} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Send times and day offsets are configured by an admin in Company Settings. Previews are
          view-only - these three emails are sent automatically by the scheduler.
        </p>
      </Section>

      <Section title="Service Termination">
        {detail.emailStamps.terminationSentAt ? (
          <div>
            <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
              <Ban className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-destructive">
                  Termination Sent
                </p>
                <p className="mt-1 text-sm">
                  {`The closure email went to the client and the Service team on ${fmtDateTime(detail.emailStamps.terminationSentAt)}. The subscription is cancelled as '${detail.cancellation.reason ?? RENEWAL_CANCELLATION_REASON.RENEWAL_UNPAID}'.`}
                </p>
              </div>
            </div>
            <RefineTerminationReasonPanel detail={detail} />
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              This is <span className="font-semibold text-foreground">not automated</span>. When the
              follow-ups have run and the client still has not paid, send this by hand: it emails
              the service-closure notice to the client, tells the Service team to disable the
              service, and cancels the subscription as{' '}
              <span className="font-semibold text-foreground">
                {RENEWAL_CANCELLATION_REASON.RENEWAL_UNPAID}
              </span>
              .
            </p>

            {!detail.canSendTermination ? (
              <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                {!verified && 'Verify the renewal before a service termination can be sent.'}
                {verified &&
                  paid &&
                  'A payment has been recorded - this renewal will not be terminated.'}
                {verified && !paid && terminated && 'This subscription has already been cancelled.'}
                {verified &&
                  !paid &&
                  !terminated &&
                  `Not available yet - the renewal is not due until ${fmtDate(detail.dueDate)}. Termination can only be sent after the due date has passed.`}
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                <div className="rounded-lg border border-warning/40 bg-warning-tint p-3 text-sm text-ink">
                  {`Overdue since ${fmtDate(detail.dueDate)}. The usual point to terminate is ${fmtDate(detail.terminationRecommendedFrom)} (due date plus the admin-configured grace period).`}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowTerminationPreview((v) => !v)}
                    aria-expanded={showTerminationPreview}
                  >
                    <Eye className="mr-1 h-3.5 w-3.5" />
                    {showTerminationPreview ? 'Hide preview' : 'Preview email'}
                  </Button>
                  {confirmTermination ? (
                    <>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={doTerminate}
                        disabled={sendTermination.isPending}
                      >
                        <Ban className="mr-1 h-3.5 w-3.5" />
                        {sendTermination.isPending ? 'Sending…' : 'Yes, terminate & notify'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmTermination(false)}
                        disabled={sendTermination.isPending}
                      >
                        Cancel
                      </Button>
                      <span className="text-[11px] font-semibold text-destructive">
                        This cannot be undone.
                      </span>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setConfirmTermination(true)}
                    >
                      <Ban className="mr-1 h-3.5 w-3.5" />
                      Send service termination
                    </Button>
                  )}
                </div>
                {showTerminationPreview && (
                  <LadderPreview
                    renewalId={detail.id}
                    kind="termination"
                    label="Service Termination"
                  />
                )}
              </div>
            )}
          </>
        )}
      </Section>

      {paid ? (
        <div className="flex items-start gap-3 rounded-lg border border-success/40 bg-success-tint p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-success">
              Payment Received
            </p>
            <p className="mt-1 text-sm font-semibold">
              {`${detail.payment.transactionNo ?? '-'} · ${detail.payment.mode ?? '-'}`}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {`Recorded ${fmtDateTime(detail.payment.paidAt)}${detail.payment.recordedByName ? ` by ${detail.payment.recordedByName}` : ''}.`}
            </p>
          </div>
        </div>
      ) : (
        verified &&
        !terminated && (
          <Section title="Record Payment">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Transaction Number</Label>
                <Input
                  value={txn}
                  onChange={(e) => setTxn(e.target.value)}
                  placeholder="UTR / reference number"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Payment Mode</Label>
                <Select value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="">- Select -</option>
                  {SETTLED_PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex items-end">
                <Button size="sm" onClick={submit} disabled={pay.isPending}>
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                  {pay.isPending ? 'Recording…' : 'Record Payment'}
                </Button>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {`Expected: ${inr(detail.pricing.totalAmount)} incl. GST per ${PERIOD_NOUN[detail.frequency]}. Recording the payment stops the remaining automated emails.`}
            </p>
          </Section>
        )
      )}
    </div>
  );
};

// ─── Step 4: Generate Invoice ────────────────────────────────────────────────
const StepInvoice = ({ detail, onDone }: AdvanceStepProps) => {
  const { toast } = useToast();
  const record = useRecordRenewalInvoice(detail.id);
  const inv = detail.invoice;
  const todayIso = new Date().toISOString().slice(0, 10);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [invoiceNumber, setInvoiceNumber] = useState(inv.invoiceNumber ?? '');
  const [invoiceDate, setInvoiceDate] = useState(inv.invoiceDate ?? todayIso);
  // Default to the GST-INCLUSIVE total - the same "Total Payable" the client
  // was asked for in the renewal emails, which is what gets invoiced.
  const [invoiceAmount, setInvoiceAmount] = useState(
    inv.invoiceAmount ?? detail.pricing.totalAmount,
  );
  const [file, setFile] = useState<{ base64: string; name: string; size: number } | null>(null);

  const onPick = async (f: File | undefined) => {
    if (!f) return;
    if (f.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('The PDF exceeds the 10 MB limit');
      return;
    }
    const base64 = await fileToBase64(f);
    setFile({ base64, name: f.name, size: f.size });
  };

  const canSave = invoiceNumber.trim().length > 0 && invoiceAmount > 0 && !!file;
  const amountMismatch = invoiceAmount !== detail.pricing.totalAmount;

  const submit = () => {
    if (!file) {
      toast.error('Upload the invoice PDF first');
      return;
    }
    record.mutate(
      {
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        invoiceAmount,
        pdfBase64: file.base64,
        pdfName: file.name,
      },
      {
        onSuccess: () => {
          toast.success('Invoice saved');
          onDone();
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not save the invoice')),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-extrabold">Generate Invoice</h2>
        <p className="text-sm text-muted-foreground">
          Upload the invoice PDF (generated in Zoho) and record its details.
        </p>
      </div>

      <Section title="Invoice Details">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Invoice Number</Label>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="INV-2026-0001"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Invoice Date</Label>
            <Input
              type="date"
              value={invoiceDate}
              max={todayIso}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{`Invoice Amount · incl. GST = ${inr(detail.pricing.totalAmount)}`}</Label>
            <NumberStepper
              value={invoiceAmount}
              onChange={setInvoiceAmount}
              min={0}
              max={1_000_000_000}
              step={100}
              inputWidthClassName="w-28"
              ariaLabel="Invoice amount"
            />
            <p className="text-[11px] text-muted-foreground">
              {`${inr(detail.pricing.amount)} + ${inr(detail.pricing.gstAmount)} GST @ ${detail.pricing.gstPercentage}%`}
            </p>
            {amountMismatch && (
              <p className="text-[11px] font-semibold text-warning">
                {`Differs from the invoiced total by ${inr(Math.abs(invoiceAmount - detail.pricing.totalAmount))}`}
              </p>
            )}
          </div>
        </div>

        <div className="mt-3 space-y-1.5">
          <Label>Invoice PDF</Label>
          {file && (
            <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2">
              <span className="inline-flex items-center gap-2 text-sm font-semibold">
                <FileText className="h-4 w-4" />
                {file.name}
                <span className="text-xs font-normal text-muted-foreground">{`${Math.round(file.size / 1024)} KB`}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => setFile(null)}>
                Remove
              </Button>
            </div>
          )}
          {!file && inv.hasInvoicePdf && (
            <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2">
              <span className="inline-flex items-center gap-2 text-sm font-semibold">
                <FileText className="h-4 w-4" />
                {inv.invoicePdfName}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  void renewalsService.downloadInvoicePdf(detail.id);
                }}
              >
                <Download className="mr-1 h-3.5 w-3.5" />
                Download
              </Button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              void onPick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            size="sm"
            className="w-full border-dashed"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mr-1.5 h-4 w-4" />
            {file || inv.hasInvoicePdf ? 'Replace PDF' : 'Upload the invoice PDF'}
          </Button>
        </div>
      </Section>

      {inv.invoiceRecordedAt && (
        <p className="text-xs text-muted-foreground">
          {`Last saved ${fmtDateTime(inv.invoiceRecordedAt)}${inv.invoiceRecordedByName ? ` by ${inv.invoiceRecordedByName}` : ''}.`}
        </p>
      )}

      <div className="flex justify-end">
        <Button size="sm" onClick={submit} disabled={!canSave || record.isPending}>
          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
          {record.isPending ? 'Saving…' : 'Save Invoice'}
        </Button>
      </div>
    </div>
  );
};

// ─── Step 5: Notify Service ──────────────────────────────────────────────────
// The notification IS an email to the Service team - previewed and edited here
// exactly like the client invoice step, then sent. Sending it stamps the step.
const StepNotify = ({ detail, onDone }: AdvanceStepProps) => {
  const { toast } = useToast();
  const notify = useNotifyRenewalService(detail.id);
  const sn = detail.serviceNotification;
  const done = !!sn.serviceNotifiedAt;
  const invoiced = !!detail.invoice.invoiceRecordedAt;

  const preview = useQuery({
    queryKey: [...QUERY_KEYS.RENEWALS.BY_ID(detail.id), 'service-preview'],
    queryFn: () => renewalsService.previewServiceNotification(detail.id),
    enabled: invoiced,
  });

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');

  useEffect(() => {
    if (preview.data?.defaultTo && !to) setTo(preview.data.defaultTo);
  }, [preview.data, to]);
  useEffect(() => {
    if (preview.data && !subject) setSubject(preview.data.subject);
  }, [preview.data, subject]);

  const submit = () => {
    if (!preview.data) return;
    const ccList = cc
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    notify.mutate(
      {
        to: [to.trim()],
        cc: ccList.length ? ccList : undefined,
        subject: subject || preview.data.subject,
        bodyHtml: preview.data.bodyHtml,
        bodyText: preview.data.bodyText,
      },
      {
        onSuccess: (r) => {
          toast.success(
            `Service queue ${r.detail.serviceNotification.serviceQueueRef ?? ''} notified`,
          );
          onDone();
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not notify the service team')),
      },
    );
  };

  if (!invoiced) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-extrabold">Notify Service</h2>
        <div className="rounded-lg border border-warning/40 bg-warning-tint p-3 text-sm text-ink">
          Record the invoice in the Generate Invoice step before notifying the service team.
        </div>
      </div>
    );
  }

  const canSend = !!preview.data && to.trim().length > 0 && !notify.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-extrabold">Notify Service</h2>
          <p className="text-sm text-muted-foreground">
            Email the service team so this client&apos;s service continues into the renewed term.
          </p>
        </div>
        <Button size="sm" onClick={submit} disabled={!canSend}>
          <Wrench className="mr-1 h-3.5 w-3.5" />
          {(() => {
            if (notify.isPending) return 'Sending…';
            return done ? 'Resend To Service Team' : 'Send To Service Team';
          })()}
        </Button>
      </div>

      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-foreground">
        Billing only forwards this renewal to the Service Team queue so service continues for the
        new cycle. The Service Team assigns the executive and priority on their end.
      </div>

      <Section title="Renewal Summary">
        <div className="grid gap-3 md:grid-cols-3">
          <ReadOnly label="Client" value={detail.client.clientName} />
          <ReadOnly label="ARN" value={detail.client.arn} />
          <ReadOnly label="Product" value={detail.pricing.productName} />
          <ReadOnly label="Invoice Number" value={detail.invoice.invoiceNumber ?? '-'} />
          <ReadOnly
            label="Invoice Amount"
            value={detail.invoice.invoiceAmount != null ? inr(detail.invoice.invoiceAmount) : '-'}
          />
          <ReadOnly label="Service Queue Ref" value={sn.serviceQueueRef ?? '-'} />
        </div>
      </Section>

      <Section title="Email">
        <div className="grid gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>To (Service Team)</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>CC (comma separated)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} />
              {/* 'internal', not 'service': the template here is
                  service-renewal-notification-internal, category 'internal',
                  which is what the server keys the CC merge on. */}
              <LockedCcChips category="internal" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <p className="text-[11px] text-muted-foreground">
            The default recipient comes from the Service Continuation template (Admin › Email
            Templates › Default To), falling back to the Service sender in Company Settings.
          </p>
        </div>
      </Section>

      <Section title="Preview">
        {preview.isLoading && <p className="text-sm text-muted-foreground">Loading preview…</p>}
        {preview.isError && (
          <p className="text-sm text-destructive">
            {errorMessage(preview.error, 'Could not load the service notification preview.')}
          </p>
        )}
        {preview.data && (
          <iframe
            title="Service notification preview"
            className="h-[360px] w-full rounded-md border border-border bg-white"
            srcDoc={preview.data.bodyHtml}
          />
        )}
      </Section>

      {done && sn.serviceQueueRef && (
        <div className="flex items-start gap-3 rounded-lg border border-success/40 bg-success-tint p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-success">
              Service Queue Entry Created
            </p>
            <p className="mt-1 font-mono text-base font-extrabold tracking-wider">
              {sn.serviceQueueRef}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {`Notified ${fmtDateTime(sn.serviceNotifiedAt)}${sn.serviceNotifiedByName ? ` by ${sn.serviceNotifiedByName}` : ''}.`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Step 6: Send Invoice ────────────────────────────────────────────────────
const StepSend = ({ detail, onDone }: AdvanceStepProps) => {
  const { toast } = useToast();
  const send = useSendRenewalInvoice(detail.id);
  const inv = detail.invoice;
  const alreadySent = !!detail.clientInvoice.sentAt;

  const preview = useQuery({
    queryKey: [...QUERY_KEYS.RENEWALS.BY_ID(detail.id), 'invoice-preview'],
    queryFn: () => renewalsService.previewInvoiceEmail(detail.id),
    enabled: !!inv.hasInvoicePdf,
  });

  const rmEmail =
    detail.client.rmEmail && detail.client.rmEmail !== '-' ? detail.client.rmEmail : '';
  const [to, setTo] = useState(detail.client.email);
  const [cc, setCc] = useState(rmEmail);
  const [subject, setSubject] = useState('');

  useEffect(() => {
    if (preview.data && !subject) setSubject(preview.data.subject);
  }, [preview.data, subject]);

  const canSend = !!inv.hasInvoicePdf && !!preview.data && to.trim().length > 0 && !send.isPending;

  const sendLabel = (() => {
    if (send.isPending) return 'Sending…';
    return alreadySent ? 'Resend Email' : 'Send Email';
  })();

  const submit = () => {
    if (!preview.data) return;
    const ccList = cc
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    send.mutate(
      {
        templateSlug: INVOICE_TEMPLATE_SLUG,
        to: [to.trim()],
        cc: ccList.length ? ccList : undefined,
        subject: subject || preview.data.subject,
        bodyHtml: preview.data.bodyHtml,
        bodyText: preview.data.bodyText,
      },
      {
        onSuccess: () => {
          toast.success(`Invoice emailed to ${to.trim()}`);
          onDone();
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not send the invoice email')),
      },
    );
  };

  if (!inv.hasInvoicePdf) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-extrabold">Send Invoice</h2>
        <div className="rounded-lg border border-warning/40 bg-warning-tint p-3 text-sm text-ink">
          Upload the invoice PDF in the Generate Invoice step before sending it to the client.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-extrabold">Send Invoice</h2>
          <p className="text-sm text-muted-foreground">
            The uploaded invoice PDF is attached automatically.
          </p>
        </div>
        <Button size="sm" onClick={submit} disabled={!canSend}>
          <Send className="mr-1 h-3.5 w-3.5" />
          {sendLabel}
        </Button>
      </div>

      {alreadySent && (
        <p className="text-xs text-emerald-600">
          {`Invoice already sent ${fmtDateTime(detail.clientInvoice.sentAt)}${detail.clientInvoice.sentByName ? ` by ${detail.clientInvoice.sentByName}` : ''}.`}
        </p>
      )}

      <Section title="Email">
        <div className="grid gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>CC (comma separated)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} />
              <LockedCcChips category="renewal" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="inline-flex w-fit items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-semibold">
            <FileText className="h-4 w-4" />
            {inv.invoicePdfName ?? 'Invoice PDF'}
            <span className="text-xs font-normal text-muted-foreground">(attached)</span>
          </div>
        </div>
      </Section>

      <Section title="Preview">
        {preview.isLoading && <p className="text-sm text-muted-foreground">Loading preview…</p>}
        {preview.isError && (
          <p className="text-sm text-destructive">
            {errorMessage(preview.error, 'Could not load the email preview.')}
          </p>
        )}
        {preview.data && (
          <iframe
            title="Renewal invoice email preview"
            className="h-[360px] w-full rounded-md border border-border bg-white"
            srcDoc={preview.data.bodyHtml}
          />
        )}
      </Section>
    </div>
  );
};

// ─── Step 7: Complete ────────────────────────────────────────────────────────
const StepComplete = ({ detail }: StepProps) => {
  const { toast } = useToast();
  const complete = useCompleteRenewal(detail.id);
  const flags = doneFlags(detail);
  const done = !!detail.completion.completedAt;
  const terminated = isTerminated(detail);
  const [nextDueDate, setNextDueDate] = useState<string | null>(null);

  const checks: { ok: boolean; label: string }[] = [
    { ok: flags.pricing, label: 'Client Details & Pricing Verified' },
    { ok: flags.schedule, label: 'Payment Received' },
    { ok: flags.invoice, label: 'Invoice Generated' },
    { ok: flags.notify, label: 'Service Team Notified' },
    { ok: flags.send, label: 'Invoice Sent To Client' },
  ];
  const allOk = checks.every((c) => c.ok);

  const submit = () => {
    complete.mutate(undefined, {
      onSuccess: (res) => {
        setNextDueDate(res.nextCycle?.dueDate ?? null);
        toast.success('Renewal completed');
      },
      onError: (error) => toast.error(errorMessage(error, 'Could not complete the renewal')),
    });
  };

  if (terminated) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-extrabold">Renewal Complete</h2>
          <p className="text-sm text-muted-foreground">This renewal did not go through.</p>
        </div>
        <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-danger-tint p-4">
          <Ban className="mt-0.5 h-6 w-6 shrink-0 text-destructive" />
          <div>
            <p className="text-sm font-bold text-destructive">Subscription Cancelled</p>
            <p className="text-xs text-muted-foreground">
              {`Cancelled ${fmtDateTime(detail.cancellation.cancelledAt ?? detail.emailStamps.terminationSentAt)}`}
              {detail.cancellation.cancelledByName
                ? ` by ${detail.cancellation.cancelledByName}.`
                : '.'}
            </p>
            {detail.cancellation.reason && (
              <p className="mt-1 break-words text-sm text-ink">
                {`Reason: ${detail.cancellation.reason}`}
                {detail.cancellation.note ? ` - ${detail.cancellation.note}` : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-extrabold">Renewal Complete</h2>
        <p className="text-sm text-muted-foreground">
          Confirm closure once every step is done. Completing spawns the next renewal cycle.
        </p>
      </div>

      {done ? (
        <div className="flex items-center gap-3 rounded-xl border border-success/30 bg-success-tint p-4">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-success" />
          <div>
            <p className="text-sm font-bold text-success">Renewal completed successfully.</p>
            <p className="text-xs text-muted-foreground">
              {`Completed ${fmtDateTime(detail.completion.completedAt)}${detail.completion.completedByName ? ` by ${detail.completion.completedByName}` : ''}.`}
            </p>
            {nextDueDate && (
              <p className="mt-1 text-sm font-semibold text-foreground">
                {`Next cycle scheduled for ${fmtDate(nextDueDate)}.`}
              </p>
            )}
          </div>
        </div>
      ) : (
        <Section title="Pre-Completion Checklist">
          <ul className="space-y-2">
            {checks.map((c) => (
              <li key={c.label} className="flex items-center gap-2 text-sm">
                <span
                  className={cn(
                    'grid h-5 w-5 place-items-center rounded-full',
                    c.ok ? 'bg-success text-white' : 'bg-muted text-muted-foreground',
                  )}
                >
                  <CheckCircle2 className="h-3 w-3" />
                </span>
                <span className={cn(c.ok ? 'font-semibold' : 'text-muted-foreground')}>
                  {c.label}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Renewal Summary">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <ReadOnly label="Client" value={detail.client.clientName} />
          <ReadOnly label="Product" value={detail.pricing.productName} />
          <ReadOnly label="Invoice Number" value={detail.invoice.invoiceNumber ?? '-'} />
          <ReadOnly
            label="Invoice Amount"
            value={detail.invoice.invoiceAmount != null ? inr(detail.invoice.invoiceAmount) : '-'}
          />
          <ReadOnly label="Frequency" value={detail.frequency} />
          <ReadOnly
            label="Service Queue"
            value={detail.serviceNotification.serviceQueueRef ?? '-'}
          />
        </div>
      </Section>

      {!done && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-background p-4">
          <StatusChip tone="warning">Awaiting final confirmation</StatusChip>
          <Button size="sm" disabled={!allOk || complete.isPending} onClick={submit}>
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
            {complete.isPending ? 'Completing…' : 'Mark Renewal Complete'}
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── Cancel Subscription (pre-payment escape hatch) ──────────────────────────
// The reason is a fixed list so churn can be grouped and reported on; 'Other'
// opens a free-text box and is the only value that requires one.
const CancelSubscriptionPanel = ({ detail }: StepProps) => {
  const { toast } = useToast();
  const cancelSubscription = useCancelSubscription(detail.id);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<RenewalCancellationReasonValue | ''>('');
  const [note, setNote] = useState('');

  const eligible =
    !detail.payment.paidAt &&
    !detail.completion.completedAt &&
    !isTerminated(detail) &&
    !isSkipped(detail);
  if (!eligible) return null;

  const needsNote = reason === 'Other';
  const canSubmit =
    !!reason && (!needsNote || note.trim().length > 0) && !cancelSubscription.isPending;

  const close = () => {
    setOpen(false);
    setReason('');
    setNote('');
  };

  const submit = () => {
    if (!reason) return;
    cancelSubscription.mutate(
      { reason, ...(note.trim() ? { note: note.trim() } : {}) },
      {
        onSuccess: () => {
          toast.success('Subscription cancelled');
          close();
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not cancel this subscription')),
      },
    );
  };

  if (!open) {
    return (
      <Button
        size="sm"
        variant="ghost"
        className="mt-2 w-full text-destructive hover:bg-danger-tint hover:text-destructive"
        onClick={() => setOpen(true)}
      >
        <Ban className="mr-1 h-3.5 w-3.5" />
        Cancel Subscription
      </Button>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-destructive/40 bg-danger-tint p-2.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-destructive">
        Cancel Subscription
      </p>
      <p className="mb-2 text-[11px] text-ink">
        Service is terminated and pending renewal emails are cancelled. This cannot be undone.
      </p>

      <div className="space-y-1.5">
        <Label className="text-[11px]">Reason</Label>
        <Select
          value={reason}
          onChange={(e) => setReason(e.target.value as RenewalCancellationReasonValue | '')}
          className="h-8 bg-background text-sm"
          aria-label="Cancellation reason"
        >
          <option value="">- Select a reason -</option>
          {BILLER_CANCELLATION_REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      </div>

      {needsNote && (
        <div className="mt-2 space-y-1.5">
          <Label className="text-[11px]">Describe the reason</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 255))}
            placeholder="Tell us why the client is cancelling…"
            className="min-h-[60px] bg-background text-sm"
          />
          <div className="text-right text-[10px] text-muted-foreground tabular-nums">
            {`${note.length}/255`}
          </div>
        </div>
      )}

      <div className="mt-2 flex gap-2">
        <Button
          size="sm"
          variant="danger"
          className="flex-1"
          disabled={!canSubmit}
          onClick={submit}
        >
          {cancelSubscription.isPending ? 'Cancelling…' : 'Confirm Cancellation'}
        </Button>
        <Button size="sm" variant="outline" className="flex-1" onClick={close}>
          Keep Active
        </Button>
      </div>
    </div>
  );
};

// ─── Deactivate / Activate (ADR-054) ─────────────────────────────────────────
// Deactivation is NOT cancellation: service is switched off but the client can
// be brought back, and when they are, the renewal clock is rebuilt from the
// unused part of the term they had already paid for.
//
// Both notices go out BY HAND and both must land before anything takes effect,
// so this is a three-stage flow rather than one button: pick a reason, send the
// client notice, send the Service-team notice.

interface DeactivationNoticeProps {
  detail: RenewalDetail;
  audience: DeactivationAudience;
  title: string;
  description: string;
  sentAt: string | null;
}

const DeactivationNotice = ({
  detail,
  audience,
  title,
  description,
  sentAt,
}: DeactivationNoticeProps) => {
  const { toast } = useToast();
  const send = useSendDeactivationEmail(detail.id);
  const done = sentAt != null;

  const preview = useQuery({
    queryKey: [...QUERY_KEYS.RENEWALS.BY_ID(detail.id), 'deactivation-preview', audience],
    queryFn: () => renewalsService.previewDeactivationEmail(detail.id, audience),
    enabled: detail.deactivation.reason != null && !done,
  });

  const rmEmail =
    detail.client.rmEmail && detail.client.rmEmail !== '-' ? detail.client.rmEmail : '';
  // The client notice goes to the CLIENT, so it is seeded from their address the
  // same way the invoice email is. A client-facing template carries no
  // `defaultTo` - that field is how the internal notices find their team inbox -
  // so waiting for it left the To box empty and the send blocked.
  const [to, setTo] = useState(audience === 'client' ? detail.client.email : '');
  const [cc, setCc] = useState(audience === 'client' ? rmEmail : '');
  const [subject, setSubject] = useState('');

  useEffect(() => {
    if (audience === 'service' && preview.data?.defaultTo && !to) setTo(preview.data.defaultTo);
  }, [audience, preview.data, to]);
  useEffect(() => {
    if (preview.data && !subject) setSubject(preview.data.subject);
  }, [preview.data, subject]);

  const submit = () => {
    if (!preview.data) return;
    const ccList = cc
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    send.mutate(
      {
        audience,
        payload: {
          to: [to.trim()],
          cc: ccList.length ? ccList : undefined,
          subject: subject || preview.data.subject,
          bodyHtml: preview.data.bodyHtml,
          bodyText: preview.data.bodyText,
        },
      },
      {
        onSuccess: (r) => {
          toast.success(
            r.detail.deactivation.deactivatedAt
              ? 'Both notices sent - the subscription is now deactivated'
              : `${title} sent`,
          );
        },
        onError: (error) =>
          toast.error(errorMessage(error, `Could not send the ${title.toLowerCase()}`)),
      },
    );
  };

  if (done) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-success/40 bg-success-tint p-3">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-success">{`${title} Sent`}</p>
          <p className="text-[11px] text-muted-foreground">{fmtDateTime(sentAt)}</p>
        </div>
      </div>
    );
  }

  const canSend = !!preview.data && to.trim().length > 0 && !send.isPending;

  return (
    <Section title={title}>
      <p className="mb-3 text-sm text-muted-foreground">{description}</p>

      <div className="grid gap-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>To</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>CC (comma separated)</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} />
            {/* service-deactivation-internal is category 'internal';
                subscription-deactivation (the client notice) is 'renewal'. */}
            <LockedCcChips category={audience === 'service' ? 'internal' : 'renewal'} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
      </div>

      <div className="mt-3">
        {preview.isLoading && <p className="text-sm text-muted-foreground">Loading preview…</p>}
        {preview.isError && (
          <p className="text-sm text-destructive">
            {errorMessage(preview.error, 'Could not load the preview.')}
          </p>
        )}
        {preview.data && (
          <iframe
            title={`${title} preview`}
            className="h-[280px] w-full rounded-md border border-border bg-white"
            srcDoc={preview.data.bodyHtml}
          />
        )}
      </div>

      <Button size="sm" className="mt-3" onClick={submit} disabled={!canSend}>
        <Mail className="mr-1 h-3.5 w-3.5" />
        {send.isPending ? 'Sending…' : `Send ${title}`}
      </Button>
    </Section>
  );
};

const DeactivationDialog = ({
  detail,
  open,
  onOpenChange,
}: {
  detail: RenewalDetail;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const { toast } = useToast();
  const start = useStartDeactivation(detail.id);
  const d = detail.deactivation;
  const [reason, setReason] = useState<RenewalCancellationReasonValue | ''>(d.reason ?? '');
  const [note, setNote] = useState(d.note ?? '');

  const needsNote = reason === 'Other';
  const canSaveReason = !!reason && (!needsNote || note.trim().length > 0) && !start.isPending;
  // The notices are only offered once a reason is on record, so a client is
  // never emailed about a decision nobody wrote down.
  const reasonRecorded = d.reason != null;

  const saveReason = () => {
    if (!reason) return;
    start.mutate(
      { reason, ...(note.trim() ? { note: note.trim() } : {}) },
      {
        onSuccess: () => toast.success('Reason recorded - now send both notices'),
        onError: (error) => toast.error(errorMessage(error, 'Could not record the reason')),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] flex-col overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Deactivate Subscription</DialogTitle>
          <DialogDescription>
            {`${detail.client.clientName} · ${detail.pricing.productName}. Service is switched off, ` +
              'but the subscription is kept - you can reactivate it later and the renewal date is ' +
              'recalculated from the unused part of the term.'}
          </DialogDescription>
        </DialogHeader>

        {d.deactivatedAt ? (
          <div className="rounded-lg border border-destructive/40 bg-danger-tint p-3">
            <p className="text-xs font-bold uppercase tracking-wider text-destructive">
              Subscription Deactivated
            </p>
            <p className="mt-1 text-sm text-ink">
              {`Deactivated ${fmtDateTime(d.deactivatedAt)}` +
                `${d.deactivatedByName ? ` by ${d.deactivatedByName}` : ''}.`}
            </p>
          </div>
        ) : (
          <>
            <Section title="Reason">
              <p className="mb-2 text-[11px] text-muted-foreground">
                Recorded for churn reporting only. Neither notice tells the client or the Service
                team why.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Reason</Label>
                  <Select
                    value={reason}
                    onChange={(e) =>
                      setReason(e.target.value as RenewalCancellationReasonValue | '')
                    }
                    aria-label="Deactivation reason"
                  >
                    <option value="">- Select a reason -</option>
                    {BILLER_CANCELLATION_REASONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                </div>
                {needsNote && (
                  <div className="space-y-1.5">
                    <Label>Describe the reason</Label>
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value.slice(0, 255))}
                      className="min-h-[60px]"
                    />
                  </div>
                )}
              </div>
              <Button size="sm" className="mt-3" onClick={saveReason} disabled={!canSaveReason}>
                {start.isPending ? 'Saving…' : 'Save Reason'}
              </Button>
            </Section>

            {reasonRecorded && (
              <>
                <DeactivationNotice
                  detail={detail}
                  audience="client"
                  title="Client Notice"
                  description="Tells the client their subscription has been deactivated and that it can be restored."
                  sentAt={d.clientSentAt}
                />
                <DeactivationNotice
                  detail={detail}
                  audience="service"
                  title="Service Team Notice"
                  description="Tells the Service team which product to switch off. Sending this one completes the deactivation."
                  sentAt={d.serviceSentAt}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * The Deactivate / Activate control in the workflow sidebar, beside Cancel
 * Subscription.
 *
 * Activation is a single click because everything it needs is already known:
 * the recalculated date is derived from the deactivation stamp, and the working
 * behind it is shown on the info icon rather than asked for.
 */
const DeactivationPanel = ({ detail }: StepProps) => {
  const { toast } = useToast();
  const reactivate = useReactivateRenewal(detail.id);
  const abort = useAbortDeactivation(detail.id);
  const [open, setOpen] = useState(false);
  const d = detail.deactivation;

  const doAbort = () => {
    abort.mutate(undefined, {
      onSuccess: () => toast.success('Deactivation called off - the subscription stays active'),
      onError: (error) => toast.error(errorMessage(error, 'Could not call off the deactivation')),
    });
  };

  const doReactivate = () => {
    reactivate.mutate(undefined, {
      onSuccess: (r) =>
        toast.success(
          `Reactivated - renews on ${fmtDate(r.dueDate)}. The workflow restarts at step 1.`,
        ),
      onError: (error) =>
        toast.error(errorMessage(error, 'Could not reactivate this subscription')),
    });
  };

  if (d.canReactivate) {
    return (
      <div className="mt-2 rounded-lg border border-destructive/40 bg-danger-tint p-2.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-destructive">
          Deactivated
        </p>
        <p className="mt-1 text-[11px] text-ink">
          {`Service switched off ${fmtDateTime(d.deactivatedAt)}` +
            `${d.deactivatedByName ? ` by ${d.deactivatedByName}` : ''}.`}
        </p>
        <Button
          size="sm"
          className="mt-2 w-full"
          onClick={doReactivate}
          disabled={reactivate.isPending}
        >
          <Power className="mr-1 h-3.5 w-3.5" />
          {reactivate.isPending ? 'Activating…' : 'Activate'}
        </Button>
        <p className="mt-1.5 text-[10px] text-muted-foreground">
          Raises a Service task to switch the product back on and restarts this renewal at step 1 on
          a recalculated date.
        </p>
      </div>
    );
  }

  if (!d.canDeactivate) return null;

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="mt-2 w-full text-destructive hover:bg-danger-tint hover:text-destructive"
        onClick={() => setOpen(true)}
      >
        <PowerOff className="mr-1 h-3.5 w-3.5" />
        {d.canAbort ? 'Continue deactivation' : 'Deactivate'}
      </Button>
      {/* A deactivation part-way through its two notices is otherwise a
          one-way street: the client has been told, and the only way to clear
          the row would be to finish switching them off. Nothing has happened
          to the subscription yet, so it can simply be dropped. */}
      {d.canAbort && (
        <>
          <Button
            size="sm"
            variant="ghost"
            className="mt-1 w-full"
            onClick={doAbort}
            disabled={abort.isPending}
          >
            <Power className="mr-1 h-3.5 w-3.5" />
            {abort.isPending ? 'Calling off…' : 'Call off deactivation'}
          </Button>
          <p className="mt-1.5 text-[10px] text-muted-foreground">
            {d.clientSentAt
              ? 'The client has been told but the Service team has not, so the product is still running. Calling it off clears the reason and both notices.'
              : 'Clears the recorded reason. Nothing has been sent yet.'}
          </p>
        </>
      )}
      <DeactivationDialog detail={detail} open={open} onOpenChange={setOpen} />
    </>
  );
};

// ─── Shell ───────────────────────────────────────────────────────────────────
const RenewalWorkflowPage = () => {
  const { id } = useParams<{ id: string }>();
  const renewalId = Number(id);
  const navigate = useNavigate();
  const { setExtra } = useBreadcrumb();
  const { data: detail, isLoading, isError } = useRenewal(renewalId);
  const { toast } = useToast();
  const refreshAum = useRefreshRenewalAum(renewalId);
  const autoRefreshedFor = useRef<number | null>(null);
  const [aumChecking, setAumChecking] = useState(false);

  const [active, setActive] = useState<StepKey>('details');
  const [initialised, setInitialised] = useState(false);

  // Opening an AUM-priced renewal that is still being chased fetches the
  // client's live AUM once, so the AUM and amount on screen are the current
  // ones. The steps wait for it: the Client Details form copies the AUM into
  // local state on mount and would otherwise show the stale figure.
  useEffect(() => {
    if (!detail || autoRefreshedFor.current === renewalId) return;
    autoRefreshedFor.current = renewalId;
    const chaseable =
      !detail.payment.paidAt &&
      !detail.completion.completedAt &&
      !detail.cancellation.cancelledAt &&
      !detail.emailStamps.terminationSentAt &&
      !detail.skip.skippedAt &&
      !detail.deactivation.deactivatedAt;
    if (!detail.pricing.isAumBased || detail.client.isNonArn || !chaseable) return;
    setAumChecking(true);
    refreshAum.mutate(undefined, {
      onSuccess: ({ outcome }) => {
        if (outcome === 'unavailable') {
          toast.warning('Live AUM is unavailable right now - showing the stored AUM');
        }
      },
      onError: (error) => toast.error(errorMessage(error, 'Could not fetch the live AUM')),
      onSettled: () => setAumChecking(false),
    });
  }, [detail, renewalId, refreshAum, toast]);

  useEffect(() => {
    if (detail && !initialised) {
      setActive(STEPS[firstIncompleteIndex(detail)].key);
      setInitialised(true);
    }
  }, [detail, initialised]);

  useEffect(() => {
    setExtra(detail ? detail.client.clientName : null);
    return () => setExtra(null);
  }, [detail, setExtra]);

  const back = () => {
    void navigate(ROUTES.RENEWALS);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        Loading renewal…
      </div>
    );
  }
  if (isError || !detail) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">This renewal could not be found.</p>
        <Button variant="outline" size="sm" onClick={back}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to renewals
        </Button>
      </div>
    );
  }

  const flags = doneFlags(detail);
  const currentIdx = firstIncompleteIndex(detail);
  const overdue = isOverdue(detail);
  const terminated = isTerminated(detail);
  const deactivated = isDeactivated(detail) && !terminated;
  const skipped = isSkipped(detail) && !terminated;
  const zeroAumHeld =
    detail.zeroAumHoldAt != null && !detail.payment.paidAt && !terminated && !deactivated && !skipped;

  const stepState = (key: StepKey, idx: number): StepStatus => {
    if (flags[key]) return 'completed';
    if (idx === currentIdx) return 'current';
    return idx < currentIdx ? 'pending' : 'blocked';
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3 lg:grid-cols-6">
          <HeaderCell label="Renewal" value={detail.renewalCode} mono />
          <HeaderCell label="Client" value={detail.client.clientName} />
          <HeaderCell
            label={detail.client.isNonArn ? 'Client Code' : 'ARN'}
            value={detail.client.arn}
            mono
          />
          <HeaderCell label="Product" value={detail.pricing.productName} />
          <HeaderCell
            label="Amount"
            value={`${inr(detail.pricing.amount)} / ${PERIOD_NOUN[detail.frequency]}`}
          />
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Due Date
            </p>
            <p
              className={cn(
                'break-words text-sm font-semibold',
                overdue ? 'text-destructive' : 'text-foreground',
              )}
            >
              {fmtDate(detail.dueDate)}
              {overdue && ' · Overdue'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusChip tone="info">{detail.frequency}</StatusChip>
          <StatusChip tone={renewalTone(detail.status)}>{detail.status}</StatusChip>
          <Button variant="outline" size="sm" onClick={back}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to renewals
          </Button>
        </div>
      </div>

      {terminated && (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-danger-tint p-4">
          <Ban className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <p className="text-sm font-bold text-destructive">
              This subscription has been cancelled.
            </p>
            <p className="text-xs text-muted-foreground">
              {`Cancelled ${fmtDateTime(detail.cancellation.cancelledAt ?? detail.emailStamps.terminationSentAt)}`}
              {detail.cancellation.cancelledByName
                ? ` by ${detail.cancellation.cancelledByName}.`
                : '.'}
              {detail.cancellation.reason ? ` Reason: ${detail.cancellation.reason}` : ''}
              {detail.cancellation.note ? ` - ${detail.cancellation.note}` : ''}
            </p>
          </div>
        </div>
      )}

      {skipped && (
        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-4">
          <SkipForward className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <div>
            <p className="text-sm font-bold text-foreground">This renewal was skipped.</p>
            <p className="text-xs text-muted-foreground">
              {`Skipped ${fmtDateTime(detail.skip.skippedAt)}`}
              {detail.skip.skippedByName ? ` by ${detail.skip.skippedByName}.` : '.'}
              {
                ' This Non-ARN client is not in the renewal-reminder flow: no emails were sent and no next cycle was created.'
              }
            </p>
          </div>
        </div>
      )}

      {deactivated && (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-danger-tint p-4">
          <PowerOff className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <p className="text-sm font-bold text-destructive">
              This subscription is deactivated - the renewal is on hold.
            </p>
            <p className="text-xs text-muted-foreground">
              {`Deactivated ${fmtDateTime(detail.deactivation.deactivatedAt)}`}
              {detail.deactivation.deactivatedByName
                ? ` by ${detail.deactivation.deactivatedByName}.`
                : '.'}
              {detail.deactivation.reason ? ` Reason: ${detail.deactivation.reason}` : ''}
              {detail.deactivation.note ? ` - ${detail.deactivation.note}` : ''}
              {
                ' Use Activate to bring the client back; the renewal then restarts at step 1 on a recalculated date.'
              }
            </p>
          </div>
        </div>
      )}

      {zeroAumHeld && (
        <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning-tint p-4">
          <PauseCircle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <div>
            <p className="text-sm font-bold text-foreground">
              Live AUM is 0 - reminder emails are on hold.
            </p>
            <p className="text-xs text-muted-foreground">
              {`AUM fetched from AMFI ${fmtDateTime(detail.client.aumFetchedAt)}.`}
              {
                ' No automated renewal reminder is sent to this client while the AUM is 0; they resume on their own once a later check finds it above 0. Listed under Renewals > Zero AUM.'
              }
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <nav className="h-fit rounded-xl border border-border bg-card p-3 lg:sticky lg:top-4">
          <div className="flex items-center justify-between px-2 pb-2 pt-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Renewal Workflow
            </p>
            <StatusChip tone={renewalTone(detail.status)}>{detail.status}</StatusChip>
          </div>

          {/* Which product/cycle this per-product renewal row covers. */}
          <div className="mb-2 rounded-lg border border-primary/30 bg-primary/5 p-2.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary">
              {`Cycle ${detail.cycleNumber}`}
            </p>
            <p className="text-sm font-bold text-foreground">{detail.pricing.productName}</p>
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
              {`Due ${fmtDate(detail.dueDate)} · ${inr(detail.pricing.amount)} / ${PERIOD_NOUN[detail.frequency]}`}
              {/* After a reactivation the date is not the one the client
                  originally bought, so the working behind it is on hover
                  rather than left for someone to reverse-engineer. */}
              {detail.deactivation.reactivationBasis && (
                <span title={detail.deactivation.reactivationBasis}>
                  <Info className="h-3.5 w-3.5 text-primary" />
                  <span className="sr-only">{detail.deactivation.reactivationBasis}</span>
                </span>
              )}
            </p>
          </div>

          {/* Cycle origin: when the billing notify-service step enabled service. */}
          <div className="mb-2 rounded-lg border border-border bg-muted/30 p-2.5">
            <p className="font-mono text-[11px] tabular-nums text-foreground">
              {fmtDate(detail.serviceEnabledAt)}
            </p>
            <p className="text-sm font-bold text-foreground">Service Enabled</p>
            <p className="mt-0.5 break-words text-[11px] text-muted-foreground">
              {`ARN ${detail.client.arn} · ${detail.client.clientName} · ${detail.termYears} yr term`}
            </p>
          </div>

          <ol className="space-y-1">
            {STEPS.map((step, idx) => {
              const st = stepState(step.key, idx);
              const clickable = st !== 'blocked';
              const isActive = active === step.key;
              const Icon = STEP_ICON[st];
              const meta = stepMetaFor(detail, step.key);
              const isDone = st === 'completed';
              return (
                <li key={step.key}>
                  <button
                    type="button"
                    disabled={!clickable}
                    onClick={() => clickable && setActive(step.key)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-lg border p-2.5 text-left transition-colors',
                      isActive
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent bg-background hover:border-border',
                      !clickable && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-bold',
                        STEP_DOT[st],
                      )}
                    >
                      {st === 'completed' || st === 'blocked' ? (
                        <Icon className="h-3.5 w-3.5" />
                      ) : (
                        idx + 1
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block text-sm font-bold', isActive && 'text-primary')}>
                        {step.title}
                      </span>
                      {isDone ? (
                        <>
                          <span className="mt-0.5 block font-mono text-[11px] tabular-nums text-muted-foreground">
                            {fmtDateTime(meta.at)}
                          </span>
                          {meta.by && (
                            <span className="block break-words text-[11px] font-semibold text-foreground">
                              {meta.by}
                            </span>
                          )}
                          {meta.detail && (
                            <span className="mt-0.5 block break-words text-[11px] leading-snug text-muted-foreground">
                              {meta.detail}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
                            {st}
                          </span>
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            {step.short}
                          </span>
                        </>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          <DeactivationPanel detail={detail} />
          <CancelSubscriptionPanel detail={detail} />
        </nav>

        <div className="min-w-0 rounded-xl border border-border bg-card p-5">
          {/* Every workflow write is refused by the server while the
              subscription is off, so the steps are replaced rather than shown
              with live buttons that can only produce an error. The client
              block, the pricing and the ladder are all re-done from step 1
              after Activate anyway - against the recalculated date. */}
          {skipped && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <SkipForward className="h-10 w-10 text-muted-foreground" />
              <p className="text-base font-extrabold text-foreground">Renewal skipped</p>
              <p className="max-w-md text-sm text-muted-foreground">
                This Non-ARN client is not in the renewal-reminder flow. The cycle is closed: no
                reminders were sent, no payment is expected, and no next cycle was created.
              </p>
            </div>
          )}
          {deactivated && !skipped && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <PowerOff className="h-10 w-10 text-destructive" />
              <p className="text-base font-extrabold text-foreground">
                Renewal workflow is on hold
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                No step can be worked while the product is switched off. Bring the subscription back
                with Activate in the panel on the left - the renewal then restarts from Client
                Details on a renewal date recalculated for the time the client lost.
              </p>
            </div>
          )}
          {!deactivated && !skipped && aumChecking && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Fetching the latest AUM from AMFI…</p>
            </div>
          )}
          {!deactivated && !skipped && !aumChecking && (
            <>
              {active === 'details' && (
                // Re-keyed on each fetch so the form re-seeds with the new AUM.
                <StepClientDetails
                  key={detail.client.aumFetchedAt ?? 'not-fetched'}
                  detail={detail}
                  onDone={() => setActive('pricing')}
                />
              )}
              {active === 'pricing' && (
                <StepPricing detail={detail} onDone={() => setActive('schedule')} />
              )}
              {active === 'schedule' && (
                <StepSchedule detail={detail} onDone={() => setActive('invoice')} />
              )}
              {active === 'invoice' && (
                <StepInvoice detail={detail} onDone={() => setActive('notify')} />
              )}
              {active === 'notify' && (
                <StepNotify detail={detail} onDone={() => setActive('send')} />
              )}
              {active === 'send' && (
                <StepSend detail={detail} onDone={() => setActive('complete')} />
              )}
              {active === 'complete' && <StepComplete detail={detail} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RenewalWorkflowPage;
